import * as THREE from 'three'
import { FloorRenderer } from './FloorRenderer'
import { WallRenderer } from './WallRenderer'
import { ZoneRenderer } from './ZoneRenderer'
import { OccupantRenderer } from './OccupantRenderer'
import { PathSegmentRenderer } from './PathSegmentRenderer'
import type { Floor, Wall, Location, Badge, PathSegment } from '../types'

const EXPLODE_MAX_OFFSET = 8

export class BuildingRenderer {
  group: THREE.Group
  floors: Map<number, FloorRenderer> = new Map()
  zones: Map<string, ZoneRenderer> = new Map()
  occupants: Map<string, OccupantRenderer> = new Map()
  wallRenderer: WallRenderer
  pathSegmentRenderer: PathSegmentRenderer
  private clock = new THREE.Clock()

  constructor(scene: THREE.Scene) {
    this.group = new THREE.Group()
    this.group.name = 'building'
    scene.add(this.group)
    this.wallRenderer = new WallRenderer()
    this.group.add(this.wallRenderer.manualWalls)
    this.group.add(this.wallRenderer.ifcWalls)
    this.pathSegmentRenderer = new PathSegmentRenderer()
    this.group.add(this.pathSegmentRenderer.group)
  }

  setFloors(floors: Floor[], floorplanUrls?: Map<number, string>) {
    // Dispose existing floors
    for (const fr of this.floors.values()) {
      this.group.remove(fr.group)
      fr.dispose()
    }
    this.floors.clear()

    // Create new floors
    for (const floor of floors) {
      const url = floorplanUrls?.get(floor.index)
      const fr = new FloorRenderer(floor, url)
      this.floors.set(floor.index, fr)
      this.group.add(fr.group)
    }
  }

  setWalls(walls: Wall[]) {
    const floorGroups = this.getFloorGroups()
    this.wallRenderer.setManualWalls(walls, floorGroups)
  }

  async setIFCWalls(geometryUrls: Record<string, string>, token?: string) {
    const floorGroups = this.getFloorGroups()
    await this.wallRenderer.loadIFCWalls(geometryUrls, floorGroups, token)
  }

  setPathSegments(segments: PathSegment[]) {
    const floorGroups = this.getFloorGroups()
    this.pathSegmentRenderer.setSegments(segments, floorGroups)
  }

  setPathSegmentsVisible(visible: boolean) {
    this.pathSegmentRenderer.setVisible(visible)
  }

  selectPathSegment(segmentId: string | null) {
    this.pathSegmentRenderer.selectSegment(segmentId)
  }

  setExplode(factor: number) {
    for (const [index, fr] of this.floors) {
      fr.setTargetY(fr.baseY + index * factor * EXPLODE_MAX_OFFSET)
    }
  }

  setSelectedFloor(index: number | null) {
    for (const [floorIndex, fr] of this.floors) {
      if (index === null) {
        fr.setOpacity(1)
      } else {
        fr.setOpacity(floorIndex === index ? 1 : 0.3)
      }
    }
  }

  setIsolatedFloor(index: number | null) {
    for (const [floorIndex, fr] of this.floors) {
      if (index === null) {
        fr.setVisible(true)
      } else {
        fr.setVisible(floorIndex === index)
      }
    }
  }

  setWallsVisible(visible: boolean) {
    this.wallRenderer.setManualWallsVisible(visible)
  }

  setIFCWallsVisible(visible: boolean) {
    this.wallRenderer.setIFCWallsVisible(visible)
  }

  setFloorplansVisible(visible: boolean) {
    for (const fr of this.floors.values()) {
      fr.setPlaneVisible(visible)
    }
  }

  setLocations(locations: Location[]) {
    // Dispose existing zones
    for (const zr of this.zones.values()) {
      zr.group.parent?.remove(zr.group)
      zr.dispose()
    }
    this.zones.clear()

    // Create new zones, add to respective floor groups
    for (const loc of locations) {
      const zr = new ZoneRenderer(loc)
      this.zones.set(loc.id, zr)
      const floorGroup = this.floors.get(loc.floor)?.group
      if (floorGroup) {
        floorGroup.add(zr.group)
      } else {
        this.group.add(zr.group)
      }
    }
  }

  setZonesVisible(visible: boolean) {
    for (const zr of this.zones.values()) {
      zr.setVisible(visible)
    }
  }

  selectZone(zoneId: string | null) {
    for (const [id, zr] of this.zones) {
      zr.setSelected(id === zoneId)
    }
  }

  setBadges(badges: Badge[], defaultFloorY: number = 0) {
    for (const occ of this.occupants.values()) {
      occ.group.parent?.remove(occ.group)
      occ.dispose()
    }
    this.occupants.clear()

    for (const badge of badges) {
      const occ = new OccupantRenderer(badge)
      this.occupants.set(badge.badge_code, occ)
      const floorGroup = this.floors.get(badge.default_floor)?.group
      if (floorGroup) {
        floorGroup.add(occ.group)
      } else {
        occ.group.position.y = defaultFloorY
        this.group.add(occ.group)
      }
    }
  }

  setOccupantsVisible(visible: boolean) {
    for (const occ of this.occupants.values()) {
      occ.setVisible(visible)
    }
  }

  /**
   * Recalculate all zone clipping against current wall meshes.
   * Called after wall changes (position, size, rotation, add, delete).
   */
  recomputeZoneClipping() {
    const wallMeshes = this.collectWallMeshes()
    if (wallMeshes.length === 0) return

    for (const zr of this.zones.values()) {
      zr.computeClippedZone(wallMeshes)
      zr.computeConeProjection(wallMeshes)
    }
  }

  /**
   * Recalculate zone clipping for a specific floor only.
   */
  recomputeZoneClippingForFloor(floorIndex: number) {
    const wallMeshes = this.collectWallMeshesForFloor(floorIndex)
    if (wallMeshes.length === 0) return

    for (const zr of this.zones.values()) {
      if (zr.data.floor === floorIndex) {
        zr.computeClippedZone(wallMeshes)
        zr.computeConeProjection(wallMeshes)
      }
    }
  }

  private collectWallMeshes(): THREE.Mesh[] {
    const meshes: THREE.Mesh[] = []
    this.group.traverse((obj) => {
      if (obj instanceof THREE.Mesh && (obj.userData.type === 'wall' || obj.userData.type === 'ifc-wall')) {
        meshes.push(obj)
      }
    })
    return meshes
  }

  private collectWallMeshesForFloor(floorIndex: number): THREE.Mesh[] {
    const meshes: THREE.Mesh[] = []
    const floorGroup = this.floors.get(floorIndex)?.group
    if (!floorGroup) return meshes

    floorGroup.traverse((obj) => {
      if (obj instanceof THREE.Mesh && (obj.userData.type === 'wall' || obj.userData.type === 'ifc-wall')) {
        meshes.push(obj)
      }
    })
    return meshes
  }

  getFloorGroup(index: number): THREE.Group | undefined {
    return this.floors.get(index)?.group
  }

  private getFloorGroups(): Map<number, THREE.Group> {
    const floorGroups = new Map<number, THREE.Group>()
    for (const [index, fr] of this.floors) {
      floorGroups.set(index, fr.group)
    }
    return floorGroups
  }

  update() {
    const dt = this.clock.getDelta()
    for (const fr of this.floors.values()) {
      fr.update(dt)
    }
    for (const occ of this.occupants.values()) {
      occ.update(dt)
    }
  }

  dispose() {
    for (const occ of this.occupants.values()) {
      occ.dispose()
    }
    this.occupants.clear()
    for (const zr of this.zones.values()) {
      zr.dispose()
    }
    this.zones.clear()
    for (const fr of this.floors.values()) {
      fr.dispose()
    }
    this.floors.clear()
    this.wallRenderer.dispose()
    this.pathSegmentRenderer.dispose()
    this.group.parent?.remove(this.group)
  }
}
